import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import DevisLifecycleView from './DevisLifecycleView';
import SitesCollectivite from './SitesCollectivite';
import TravauxDepannage from './TravauxDepannage';
import {
  Building2,
  HardHat,
  Users,
  Plus,
  Search,
  FileText,
  AlertTriangle,
  MessageSquare,
  ChevronRight,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Euro,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Building,
  Inbox,
  Send,
  RefreshCw,
  Briefcase,
  X,
  Save,
  Filter,
  MoreVertical,
  Bell,
  FileWarning,
  Star,
  Activity,
  Sparkles,
  Brain,
  Download,
  ExternalLink,
  Loader2,
  Wrench,
  Settings
} from 'lucide-react';

// Category definitions
const CATEGORIES = {
  collectivites: {
    id: 'collectivites',
    name: 'Collectivités',
    subtitle: 'Mairies, Communes, Services Publics',
    icon: Building2,
    color: 'blue',
    bgGradient: 'from-blue-600 to-blue-800',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200'
  },
  conducteurs: {
    id: 'conducteurs',
    name: 'Conducteurs de Travaux',
    subtitle: 'Maîtres d\'œuvre, Chefs de Projet',
    icon: HardHat,
    color: 'amber',
    bgGradient: 'from-amber-500 to-orange-600',
    lightBg: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200'
  }
};

export default function GestionClients() {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState('categories'); // 'categories', 'clients', 'detail', 'devis_lifecycle', 'sites'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDevisId, setSelectedDevisId] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [stats, setStats] = useState({});
  const [categoryStats, setCategoryStats] = useState({ by_category: {}, totals: {} });

  const openEditModal = () => {
    if (!selectedClient) return;
    setEditForm({
      nom: selectedClient.nom || '',
      contact_principal: selectedClient.contact_principal || '',
      telephone: selectedClient.telephone || '',
      email: selectedClient.email || '',
      adresse: selectedClient.adresse || '',
      ville: selectedClient.ville || '',
      code_postal: selectedClient.code_postal || '',
      siret: selectedClient.siret || '',
      notes: selectedClient.notes || '',
    });
    setShowEditModal(true);
  };

  const saveClientEdit = async () => {
    if (!selectedClient || !editForm) return;
    const cid = selectedClient._id || selectedClient.contact_id;
    setSavingEdit(true);
    try {
      await api.patch(`/gestion-clients/${cid}`, editForm);
      // Optimistic local update
      setSelectedClient({ ...selectedClient, ...editForm });
      setShowEditModal(false);
      // Reload list silently to keep counts in sync
      if (selectedCategory) loadClients(selectedCategory.id);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingEdit(false);
    }
  };

  // Load global stats by category on first render
  useEffect(() => {
    api.get('/gestion-clients/stats-by-category')
      .then((r) => setCategoryStats(r.data || { by_category: {}, totals: {} }))
      .catch(() => {});
  }, []);

  // Auto-select category from URL param (?category=collectivites)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat && CATEGORIES[cat] && !selectedCategory) {
      handleSelectCategory(cat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  
  // New client form
  const [newClient, setNewClient] = useState({
    nom: '',
    contact_principal: '',
    telephone: '',
    email: '',
    adresse: '',
    ville: '',
    code_postal: '',
    siret: '',
    notes: ''
  });
  
  // New observation
  const [newObservation, setNewObservation] = useState('');
  
  // New alert
  const [newAlert, setNewAlert] = useState({
    titre: '',
    description: '',
    priorite: 'medium',
    date_echeance: ''
  });
  
  // Email history
  const [emailHistory, setEmailHistory] = useState([]);
  const [showDomainSettings, setShowDomainSettings] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  // AI Analysis
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [showAISuggestionsModal, setShowAISuggestionsModal] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [autoDetectResult, setAutoDetectResult] = useState(null);
  
  // Actions en attente modal
  const [showActionModal, setShowActionModal] = useState(false);
  const [newAction, setNewAction] = useState({
    action: '',
    priorite: 'medium',
    contexte: ''
  });
  
  // Portal access modal
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [portalForm, setPortalForm] = useState({ email: '', password: '', contact_name: '' });
  const [portalSaving, setPortalSaving] = useState(false);

  // Load clients by category
  const loadClients = useCallback(async (categoryId) => {
    setLoading(true);
    try {
      const response = await api.get(`/gestion-clients/category/${categoryId}`);
      setClients(response.data.clients || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
    setLoading(false);
  }, []);

  // Load client details
  const loadClientDetails = useCallback(async (clientId) => {
    setLoading(true);
    setAutoDetectResult(null);
    try {
      const response = await api.get(`/gestion-clients/client/${clientId}`);
      setSelectedClient(response.data);
      
      // Load email history if client has email - pass client data for auto-detection
      if (response.data.email) {
        loadEmailHistory(response.data.email, response.data);
      }
    } catch (error) {
      console.error('Error loading client details:', error);
    }
    setLoading(false);
  }, []);

  // Load email history for a client - search by domain to get ALL related emails
  const loadEmailHistory = async (email, clientData) => {
    if (!clientData) return;
    const clientId = clientData._id || clientData.contact_id;
    if (!clientId) return;

    setLoadingEmails(true);
    try {
      // New endpoint: searches by main email + email_domains list (configurable per collectivité)
      const response = await api.get(`/gestion-clients/${clientId}/emails?limit=300`);
      const emails = response.data.emails || [];
      setEmailHistory(emails);

      // Auto-detect actions from emails if we have emails
      if (emails.length > 0) {
        autoDetectActionsFromEmails(emails, clientData);
      }
    } catch (error) {
      console.error('Error loading email history:', error);
      setEmailHistory([]);
    }
    setLoadingEmails(false);
  };
  
  // Auto-detect actions en attente from email exchanges
  const autoDetectActionsFromEmails = async (emails, clientData) => {
    const clientId = clientData._id || clientData.contact_id;
    if (!clientId || !emails.length) return;
    
    setAutoDetecting(true);
    try {
      const response = await api.post(`/gestion-clients/${clientId}/auto-detect-actions`, {
        client_id: clientId,
        client_name: clientData.nom,
        emails: emails.map(e => ({
          direction: e.direction,
          subject: e.subject,
          body: e.body_text || e.body_preview || '',
          date: e.date,
          from: e.from,
          to: e.to
        }))
      });
      
      const result = response.data;
      if (result.count > 0) {
        setAutoDetectResult(result);
        // Reload client details to show new actions
        const refreshed = await api.get(`/gestion-clients/client/${clientId}`);
        setSelectedClient(refreshed.data);
      }
    } catch (error) {
      console.error('Error auto-detecting actions:', error);
    }
    setAutoDetecting(false);
  };
  
  // Analyze emails with AI
  const handleAnalyzeEmailsWithAI = async () => {
    if (emailHistory.length === 0) return;
    
    setAnalyzingWithAI(true);
    try {
      const response = await api.post('/gestion-clients/analyze-emails', {
        client_id: selectedClient._id || selectedClient.contact_id,
        client_name: selectedClient.nom,
        emails: emailHistory.map(e => ({
          direction: e.direction,
          subject: e.subject,
          body: e.body_text || e.body_preview || '',
          date: e.date,
          from: e.from,
          to: e.to
        }))
      });
      
      setAiSuggestions(response.data);
      setShowAISuggestionsModal(true);
    } catch (error) {
      console.error('Error analyzing emails:', error);
    }
    setAnalyzingWithAI(false);
  };
  
  // Apply AI suggestions (add alerts, observations, and actions en attente)
  const handleApplyAISuggestions = async () => {
    if (!aiSuggestions) return;
    
    try {
      const clientId = selectedClient._id || selectedClient.contact_id;
      
      // Add suggested alerts
      for (const alert of aiSuggestions.alerts || []) {
        await api.post(`/gestion-clients/client/${clientId}/alert`, {
          titre: alert.titre,
          description: alert.description,
          priorite: alert.priorite || 'medium',
          date_echeance: alert.date_echeance || ''
        });
      }
      
      // Add suggested observations
      for (const obs of aiSuggestions.observations || []) {
        await api.post(`/gestion-clients/client/${clientId}/observation`, {
          contenu: obs
        });
      }
      
      // Add suggested actions en attente
      for (const action of aiSuggestions.actions_en_attente || []) {
        await api.post(`/gestion-clients/client/${clientId}/action-en-attente`, {
          action: action.action,
          priorite: action.priorite || 'medium',
          contexte: action.contexte || ''
        });
      }
      
      // Reload client details
      loadClientDetails(clientId);
      setShowAISuggestionsModal(false);
      setAiSuggestions(null);
    } catch (error) {
      console.error('Error applying AI suggestions:', error);
    }
  };

  // Handle category selection
  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(CATEGORIES[categoryId]);
    setView('clients');
    loadClients(categoryId);
    setEmailHistory([]); // Reset email history
  };

  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setView('detail');
    loadClientDetails(client._id || client.contact_id);
  };

  // Add new client
  const handleAddClient = async () => {
    try {
      await api.post('/gestion-clients/client', {
        ...newClient,
        category: selectedCategory.id
      });
      setShowAddModal(false);
      setNewClient({
        nom: '',
        contact_principal: '',
        telephone: '',
        email: '',
        adresse: '',
        ville: '',
        code_postal: '',
        siret: '',
        notes: ''
      });
      loadClients(selectedCategory.id);
    } catch (error) {
      console.error('Error adding client:', error);
    }
  };

  // Add observation
  const handleAddObservation = async () => {
    if (!newObservation.trim()) return;
    try {
      await api.post(`/gestion-clients/client/${selectedClient._id || selectedClient.contact_id}/observation`, {
        contenu: newObservation
      });
      setShowObservationModal(false);
      setNewObservation('');
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      console.error('Error adding observation:', error);
    }
  };

  // Add alert
  const handleAddAlert = async () => {
    if (!newAlert.titre.trim()) return;
    try {
      await api.post(`/gestion-clients/client/${selectedClient._id || selectedClient.contact_id}/alert`, newAlert);
      setShowAlertModal(false);
      setNewAlert({ titre: '', description: '', priorite: 'medium', date_echeance: '' });
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      console.error('Error adding alert:', error);
    }
  };

  // Mark alert as done
  const handleCompleteAlert = async (alertId) => {
    try {
      await api.put(`/gestion-clients/alert/${alertId}/complete`);
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      console.error('Error completing alert:', error);
    }
  };

  // Add action en attente
  const handleAddAction = async () => {
    if (!newAction.action.trim()) return;
    try {
      await api.post(`/gestion-clients/client/${selectedClient._id || selectedClient.contact_id}/action-en-attente`, newAction);
      setShowActionModal(false);
      setNewAction({ action: '', priorite: 'medium', contexte: '' });
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      console.error('Error adding action:', error);
    }
  };

  // Complete action en attente
  const handleCompleteAction = async (actionId) => {
    try {
      await api.put(`/gestion-clients/action/${actionId}/complete`);
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      console.error('Error completing action:', error);
    }
  };

  // Delete action en attente
  const handleDeleteAction = async (actionId) => {
    try {
      await api.delete(`/gestion-clients/action/${actionId}`);
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      console.error('Error deleting action:', error);
    }
  };

  // Create portal access
  const handleCreatePortalAccess = async () => {
    if (!portalForm.email || !portalForm.password) return;
    setPortalSaving(true);
    try {
      await api.post('/portail/admin/create-access', {
        gestion_client_id: selectedClient._id || selectedClient.contact_id,
        email: portalForm.email,
        password: portalForm.password,
        contact_name: portalForm.contact_name || selectedClient.contact_principal
      });
      setShowPortalModal(false);
      setPortalForm({ email: '', password: '', contact_name: '' });
      loadClientDetails(selectedClient._id || selectedClient.contact_id);
    } catch (error) {
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Erreur');
    }
    setPortalSaving(false);
  };

  // Filter clients by search
  const filteredClients = clients.filter(c => 
    c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contact_principal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ville?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Go back
  const handleBack = () => {
    if (view === 'devis_lifecycle') {
      setView('detail');
      setSelectedDevisId(null);
    } else if (view === 'sites') {
      setView('detail');
    } else if (view === 'travaux') {
      setView('detail');
    } else if (view === 'detail') {
      setView('clients');
      setSelectedClient(null);
    } else if (view === 'clients') {
      setView('categories');
      setSelectedCategory(null);
      setClients([]);
    }
  };

  // Categories view
  const renderCategoriesView = () => (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion des Clients</h1>
          <p className="text-slate-500 mt-1">Gérez vos clients par catégorie</p>
        </div>
      </div>

      {/* Category Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {Object.values(CATEGORIES).map((category) => {
          const Icon = category.icon;
          const cs = categoryStats.by_category?.[category.id] || {};
          return (
            <div
              key={category.id}
              onClick={() => handleSelectCategory(category.id)}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${category.bgGradient} p-8 cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-2xl group`}
              data-testid={`category-${category.id}`}
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <ChevronRight className="w-6 h-6 text-white/60 group-hover:translate-x-1 transition-transform" />
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">{category.name}</h2>
                <p className="text-white/80 text-sm">{category.subtitle}</p>
                
                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-3xl font-bold text-white" data-testid={`cat-${category.id}-clients`}>{cs.clients ?? 0}</p>
                      <p className="text-white/60 text-sm">Clients</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white" data-testid={`cat-${category.id}-devis`}>{cs.devis_actifs ?? 0}</p>
                      <p className="text-white/60 text-sm">Devis actifs</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white" data-testid={`cat-${category.id}-alertes`}>{cs.alertes ?? 0}</p>
                      <p className="text-white/60 text-sm">Alertes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats — totaux globaux (CA caché tant qu'aucun devis n'est signé) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Building2 className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="quickstats-clients">{categoryStats.totals?.clients ?? 0}</p>
              <p className="text-slate-500 text-sm">Clients suivis</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="quickstats-devis-actifs">{categoryStats.totals?.devis_actifs ?? 0}</p>
              <p className="text-slate-500 text-sm">Devis actifs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="quickstats-alertes">{categoryStats.totals?.alertes ?? 0}</p>
              <p className="text-slate-500 text-sm">Alertes actives</p>
            </div>
          </div>
        </div>
        {(categoryStats.totals?.nb_devis_signes ?? 0) > 0 ? (
          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="quickstats-ca-card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Euro className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {(categoryStats.totals.ca_signe / 1000).toFixed(0)}k€
                </p>
                <p className="text-slate-500 text-sm">CA signé ({categoryStats.totals.nb_devis_signes} devis)</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 rounded-xl p-6 border border-dashed border-slate-200" data-testid="quickstats-ca-empty">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-200 rounded-lg">
                <Euro className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 italic">Aucun devis signé</p>
                <p className="text-xs text-slate-400">Le CA s'affichera dès la 1ʳᵉ signature</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Clients list view
  const renderClientsView = () => {
    if (!selectedCategory) return null;
    const CategoryIcon = selectedCategory.icon;
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${selectedCategory.bgGradient}`}>
              <CategoryIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{selectedCategory.name}</h1>
              <p className="text-slate-500">{filteredClients.length} clients</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowAddModal(true)}
            className={`bg-gradient-to-r ${selectedCategory.bgGradient}`}
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau client
          </Button>
        </div>

        {/* Search & filters */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un client..."
              className="pl-10"
              data-testid="search-client-input"
            />
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filtres
          </Button>
        </div>

        {/* Stats bar */}
        <div className={`${selectedCategory.lightBg} rounded-xl p-4 flex items-center justify-between`}>
          <div className="flex items-center gap-8">
            <div>
              <p className={`text-2xl font-bold ${selectedCategory.textColor}`}>{stats.total_clients || 0}</p>
              <p className="text-slate-600 text-sm">Clients</p>
            </div>
            <div className="h-10 w-px bg-slate-300" />
            <div>
              <p className={`text-2xl font-bold ${selectedCategory.textColor}`}>{stats.total_devis || 0}</p>
              <p className="text-slate-600 text-sm">Devis</p>
            </div>
            <div className="h-10 w-px bg-slate-300" />
            <div>
              <p className={`text-2xl font-bold ${selectedCategory.textColor}`}>{stats.ca_total ? `${(stats.ca_total/1000).toFixed(0)}k€` : '0€'}</p>
              <p className="text-slate-600 text-sm">CA Total</p>
            </div>
            <div className="h-10 w-px bg-slate-300" />
            <div>
              <p className={`text-2xl font-bold text-amber-600`}>{stats.alerts_active || 0}</p>
              <p className="text-slate-600 text-sm">Alertes</p>
            </div>
          </div>
        </div>

        {/* Clients list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-600 rounded-full mx-auto" />
            <p className="text-slate-500 mt-4">Chargement...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun client</h3>
            <p className="text-slate-500 mb-6">Commencez par ajouter votre premier client</p>
            <Button onClick={() => setShowAddModal(true)} className={`bg-gradient-to-r ${selectedCategory.bgGradient}`}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un client
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredClients.map((client) => (
              <div
                key={client._id || client.contact_id}
                onClick={() => handleSelectClient(client)}
                className={`bg-white rounded-xl p-6 border ${selectedCategory.borderColor} hover:shadow-lg transition-all cursor-pointer group`}
                data-testid={`client-card-${client._id || client.contact_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl ${selectedCategory.lightBg} flex items-center justify-center`}>
                      <span className={`text-xl font-bold ${selectedCategory.textColor}`}>
                        {client.nom?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{client.nom}</h3>
                      {client.contact_principal && (
                        <p className="text-slate-500 text-sm">{client.contact_principal}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1">
                        {client.ville && (
                          <span className="text-slate-400 text-sm flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {client.ville}
                          </span>
                        )}
                        {client.telephone && (
                          <span className="text-slate-400 text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {client.telephone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Quick stats */}
                    <div className="text-center">
                      <p className="text-lg font-semibold text-slate-900">{client.devis_count || 0}</p>
                      <p className="text-xs text-slate-500">Devis</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-green-600">{client.ca_total ? `${(client.ca_total/1000).toFixed(1)}k€` : '0€'}</p>
                      <p className="text-xs text-slate-500">CA</p>
                    </div>
                    {client.alerts_count > 0 && (
                      <div className="flex items-center gap-1 px-3 py-1 bg-amber-100 rounded-full">
                        <Bell className="w-4 h-4 text-amber-600" />
                        <span className="text-amber-700 font-medium">{client.alerts_count}</span>
                      </div>
                    )}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Supprimer définitivement "${client.nom}" ? Les devis/factures associés ne seront pas supprimés, seulement déliés.`)) return;
                        try {
                          const cid = client._id || client.contact_id;
                          await api.delete(`/gestion-clients/${cid}`);
                          toast.success('Collectivité supprimée');
                          loadClients(selectedCategory.id);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Erreur suppression');
                        }
                      }}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer cette fiche"
                      data-testid={`delete-client-${client._id || client.contact_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Client detail view
  const renderDetailView = () => {
    if (!selectedClient || !selectedCategory) return null;
    const CategoryIcon = selectedCategory.icon;
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className={`w-16 h-16 rounded-xl ${selectedCategory.lightBg} flex items-center justify-center`}>
              <span className={`text-2xl font-bold ${selectedCategory.textColor}`}>
                {selectedClient.nom?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{selectedClient.nom}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedCategory.lightBg} ${selectedCategory.textColor}`}>
                  {selectedCategory.name}
                </span>
                {selectedClient.contact_principal && (
                  <span className="text-slate-500">• {selectedClient.contact_principal}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {!selectedClient.portal_access && (
              <Button 
                variant="outline" 
                className="border-green-300 text-green-700 hover:bg-green-50"
                onClick={() => {
                  setPortalForm({ email: selectedClient.email || '', password: '', contact_name: selectedClient.contact_principal || '' });
                  setShowPortalModal(true);
                }}
                data-testid="create-portal-access-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Créer accès portail
              </Button>
            )}
            {selectedClient.portal_access && (
              <span className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Portail actif
              </span>
            )}
            <Button variant="outline" onClick={openEditModal} data-testid="edit-client-btn">
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={() => setView('travaux')}
            className="flex-1 min-w-[150px] bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
            data-testid="view-travaux-btn"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Dossier En Cours
          </Button>
          <Button 
            onClick={() => setView('sites')}
            variant="outline"
            className="flex-1 min-w-[150px] border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="view-sites-btn"
          >
            <Building className="w-4 h-4 mr-2" />
            Sites & Bâtiments
          </Button>
          <Button 
            onClick={() => setShowObservationModal(true)}
            variant="outline"
            className="flex-1 min-w-[120px]"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Observation
          </Button>
          <Button 
            onClick={() => setShowAlertModal(true)}
            variant="outline"
            className="flex-1 min-w-[120px] border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <Bell className="w-4 h-4 mr-2" />
            Alerte
          </Button>
          <Button 
            onClick={() => navigate('/devis')}
            className={`flex-1 min-w-[120px] bg-gradient-to-r ${selectedCategory.bgGradient}`}
          >
            <FileText className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{selectedClient.devis_count || 0}</p>
                <p className="text-slate-500 text-sm">Devis total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Euro className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{selectedClient.ca_total ? `${(selectedClient.ca_total).toLocaleString()}€` : '0€'}</p>
                <p className="text-slate-500 text-sm">CA généré</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{selectedClient.taux_conversion || 0}%</p>
                <p className="text-slate-500 text-sm">Taux conversion</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{selectedClient.alerts?.filter(a => !a.completed)?.length || 0}</p>
                <p className="text-slate-500 text-sm">Alertes actives</p>
              </div>
            </div>
          </div>
        </div>

        {/* ACTIONS EN ATTENTE DE NOTRE PART - Prominent section */}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-300" data-testid="actions-en-attente-section">
          <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              Actions en attente de notre part
              {selectedClient.actions_en_attente?.filter(a => !a.completed)?.length > 0 && (
                <span className="ml-2 px-3 py-1 bg-orange-500 text-white text-sm font-bold rounded-full">
                  {selectedClient.actions_en_attente.filter(a => !a.completed).length}
                </span>
              )}
              {autoDetecting && (
                <span className="ml-2 flex items-center gap-1 text-sm font-normal text-purple-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyse des emails en cours...
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {autoDetectResult && autoDetectResult.count > 0 && (
                <span className="text-sm text-green-600 font-medium bg-green-50 px-3 py-1 rounded-full">
                  {autoDetectResult.count} action(s) détectée(s) par IA
                </span>
              )}
              <Button 
                size="sm" 
                onClick={() => setShowActionModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="add-action-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </div>
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {selectedClient.actions_en_attente?.filter(a => !a.completed)?.length > 0 ? (
              selectedClient.actions_en_attente.filter(a => !a.completed).map((action, idx) => (
                <div 
                  key={action._id || idx}
                  className={`p-4 rounded-lg border bg-white ${
                    action.priorite === 'high' ? 'border-red-300 shadow-sm shadow-red-100' : 
                    action.priorite === 'medium' ? 'border-orange-300 shadow-sm shadow-orange-100' : 
                    'border-slate-200'
                  }`}
                  data-testid={`action-item-${action._id || idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${
                          action.priorite === 'high' ? 'bg-red-500' : 
                          action.priorite === 'medium' ? 'bg-orange-500' : 
                          'bg-blue-500'
                        }`} />
                        <p className="font-semibold text-slate-900">{action.action}</p>
                        {action.auto_detected && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs font-medium rounded">
                            IA
                          </span>
                        )}
                      </div>
                      {action.contexte && (
                        <p className="text-sm text-slate-500 ml-4 italic">
                          {action.contexte}
                        </p>
                      )}
                      {action.created_at && (
                        <p className="text-xs text-slate-400 mt-1 ml-4">
                          Ajouté le {new Date(action.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleCompleteAction(action._id)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid={`complete-action-${action._id || idx}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDeleteAction(action._id)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 bg-white/50 rounded-lg">
                <Briefcase className="w-8 h-8 text-orange-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">Aucune action en attente</p>
                <p className="text-slate-400 text-xs mt-1">Utilisez l'analyse IA pour détecter automatiquement les actions</p>
              </div>
            )}
            {/* Show completed actions collapsed */}
            {selectedClient.actions_en_attente?.filter(a => a.completed)?.length > 0 && (
              <div className="pt-2 border-t border-orange-200">
                <p className="text-xs text-slate-400 mb-2">
                  {selectedClient.actions_en_attente.filter(a => a.completed).length} action(s) complétée(s)
                </p>
                {selectedClient.actions_en_attente.filter(a => a.completed).slice(0, 3).map((action, idx) => (
                  <div key={action._id || idx} className="p-2 rounded bg-slate-50 text-slate-400 text-sm line-through mb-1">
                    {action.action}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact info */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Informations de contact
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {selectedClient.telephone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">{selectedClient.telephone}</span>
              </div>
            )}
            {selectedClient.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">{selectedClient.email}</span>
              </div>
            )}
            {(selectedClient.adresse || selectedClient.ville) && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">
                  {selectedClient.adresse}{selectedClient.ville && `, ${selectedClient.ville}`}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Alerts section */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Alertes
              </span>
              <Button size="sm" variant="ghost" onClick={() => setShowAlertModal(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            </h3>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {selectedClient.alerts?.length > 0 ? (
                selectedClient.alerts.map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${alert.completed ? 'bg-slate-50 border-slate-200' : 
                      alert.priorite === 'high' ? 'bg-red-50 border-red-200' : 
                      alert.priorite === 'medium' ? 'bg-amber-50 border-amber-200' : 
                      'bg-blue-50 border-blue-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-medium ${alert.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                          {alert.titre}
                        </p>
                        {alert.description && (
                          <p className="text-sm text-slate-600 mt-1">{alert.description}</p>
                        )}
                        {alert.date_echeance && (
                          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Échéance: {new Date(alert.date_echeance).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                      {!alert.completed && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleCompleteAlert(alert._id)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">Aucune alerte</p>
              )}
            </div>
          </div>

          {/* Observations section */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                Observations
              </span>
              <Button size="sm" variant="ghost" onClick={() => setShowObservationModal(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            </h3>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {selectedClient.observations?.length > 0 ? (
                selectedClient.observations.map((obs, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-slate-700">{obs.contenu}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(obs.date).toLocaleDateString('fr-FR', { 
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">Aucune observation</p>
              )}
            </div>
          </div>
        </div>

        {/* Email History Section */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-purple-500" />
              Historique des échanges email
              {selectedClient.email && (
                <span className="text-xs font-normal text-slate-400">({selectedClient.email})</span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDomainSettings(true)}
                className="text-slate-600"
                title="Paramètres domaines email"
                data-testid="open-email-domains-btn"
              >
                <Settings className="w-4 h-4 mr-1" /> Domaines
              </Button>
              {emailHistory.length > 0 && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleAnalyzeEmailsWithAI}
                  disabled={analyzingWithAI}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {analyzingWithAI ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-1" />
                  )}
                  Analyser avec IA
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => loadEmailHistory(selectedClient.email, selectedClient)}
                disabled={loadingEmails}
              >
                <RefreshCw className={`w-4 h-4 ${loadingEmails ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </h3>

          {/* Show currently configured domains */}
          {(selectedClient.email_domains?.length > 0) && (
            <div className="mb-3 flex flex-wrap gap-1.5 items-center text-xs">
              <span className="text-slate-500">Domaines surveillés :</span>
              {selectedClient.email_domains.map((d) => (
                <span key={d} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-mono">@{d}</span>
              ))}
            </div>
          )}

          {(!selectedClient.email && !(selectedClient.email_domains?.length > 0)) ? (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucun email ni domaine configuré</p>
              <p className="text-slate-400 text-xs mt-1 mb-3">Ajoutez un domaine pour voir tous les @manteslaville.fr / @merysuroise.fr</p>
              <Button size="sm" onClick={() => setShowDomainSettings(true)}>
                <Settings className="w-3.5 h-3.5 mr-1" /> Configurer les domaines
              </Button>
            </div>
          ) : loadingEmails ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
              <p className="text-slate-500 text-sm">Chargement des emails...</p>
            </div>
          ) : emailHistory.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {emailHistory.map((email, idx) => (
                <div 
                  key={idx}
                  onClick={() => {
                    setSelectedEmail(email);
                    setShowEmailModal(true);
                  }}
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    email.direction === 'sent' 
                      ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' 
                      : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      email.direction === 'sent' ? 'bg-blue-100' : 'bg-purple-100'
                    }`}>
                      {email.direction === 'sent' ? (
                        <Send className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Inbox className="w-4 h-4 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            email.direction === 'sent' 
                              ? 'bg-blue-200 text-blue-700' 
                              : 'bg-purple-200 text-purple-700'
                          }`}>
                            {email.direction === 'sent' ? 'Envoyé' : 'Reçu'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {email.date ? new Date(email.date).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : 'Date inconnue'}
                          </span>
                        </div>
                        <Eye className="w-4 h-4 text-slate-400" />
                      </div>
                      <p className="font-medium text-slate-900 text-sm truncate">
                        {email.subject || 'Sans objet'}
                      </p>
                      {email.body_preview && (
                        <p className="text-slate-600 text-xs mt-1 line-clamp-2">
                          {email.body_preview.replace(/<[^>]*>/g, '').substring(0, 150)}...
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                        {email.direction === 'sent' ? (
                          <span>→ {Array.isArray(email.to) ? email.to.join(', ') : email.to}</span>
                        ) : (
                          <span>← {email.from}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucun échange email trouvé</p>
              <p className="text-slate-400 text-xs mt-1">Les emails avec {selectedClient.email} apparaîtront ici</p>
            </div>
          )}
        </div>

        {/* Devis section */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-green-500" />
              Historique des devis
            </span>
            <Button size="sm" variant="outline" onClick={() => navigate('/devis')}>
              Voir tous
            </Button>
          </h3>
          <div className="space-y-3">
            {selectedClient.devis?.length > 0 ? (
              selectedClient.devis.slice(0, 10).map((devis, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 hover:shadow-md transition-all"
                >
                  <div 
                    className="flex items-center gap-4 flex-1 cursor-pointer"
                    onClick={() => {
                      setSelectedDevisId(devis.devis_id || devis._id);
                      setView('devis_lifecycle');
                    }}
                  >
                    <div className="p-2 bg-white rounded-lg border border-slate-200">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{devis.devis_id || devis.numero}</p>
                        {/* Status badge */}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          devis.statut === 'Accepté' ? 'bg-green-100 text-green-700' :
                          devis.statut === 'Refusé' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {devis.statut || 'En attente'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">{devis.project_title || 'Sans titre'}</p>
                      {devis.statut_observation && (
                        <p className="text-xs text-slate-400 italic mt-1">"{devis.statut_observation}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{devis.totaux?.ttc?.toLocaleString() || devis.montant_estime?.toLocaleString() || 0}€</p>
                      <p className="text-xs text-slate-500">
                        {new Date(devis.date_creation || devis.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/devis/${devis.devis_id || devis._id}/pdf`, '_blank');
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/devis/${devis.devis_id || devis._id}`);
                        }}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">Aucun devis</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="gestion-clients-page">
      {/* Single conditional render: only ONE view is mounted at any time.
          This avoids the React `insertBefore NotFoundError` triggered by the
          Emergent visual editor injecting <span data-ve-dynamic="true"> between
          sibling `&&`-rendered branches. */}
      {(() => {
        if (view === 'categories') return <div key="view-categories">{renderCategoriesView()}</div>;
        if (view === 'clients') return <div key="view-clients">{renderClientsView()}</div>;
        if (view === 'detail') return <div key="view-detail">{renderDetailView()}</div>;
        if (view === 'devis_lifecycle' && selectedDevisId) {
          return (
            <div key="view-devis-lifecycle">
              <DevisLifecycleView
                devisId={selectedDevisId}
                onBack={handleBack}
              />
            </div>
          );
        }
        if (view === 'sites' && selectedClient) {
          return (
            <div key="view-sites">
              <SitesCollectivite
                clientId={selectedClient._id || selectedClient.contact_id}
                clientName={selectedClient.nom}
                onBack={handleBack}
              />
            </div>
          );
        }
        if (view === 'travaux' && selectedClient) {
          return (
            <div key="view-travaux">
              <TravauxDepannage
                clientId={selectedClient._id || selectedClient.contact_id}
                clientName={selectedClient.nom}
                onBack={handleBack}
              />
            </div>
          );
        }
        return null;
      })()}

      {/* Email Domains Settings Dialog */}
      {showDomainSettings && selectedClient && (
        <EmailDomainsDialog
          client={selectedClient}
          onClose={() => setShowDomainSettings(false)}
          onSaved={async (newDomains) => {
            const updated = { ...selectedClient, email_domains: newDomains };
            setSelectedClient(updated);
            setShowDomainSettings(false);
            // Refresh client list + reload email history
            try {
              await loadEmailHistory(updated.email, updated);
              // Refresh categoryStats
              const r = await api.get('/gestion-clients/stats-by-category');
              setCategoryStats(r.data || { by_category: {}, totals: {} });
            } catch (e) { console.error(e); }
          }}
        />
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Nouveau client</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                <Input
                  value={newClient.nom}
                  onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                  placeholder="Ex: Mairie de Paris, Bouygues Bâtiment..."
                  data-testid="new-client-nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contact principal</label>
                <Input
                  value={newClient.contact_principal}
                  onChange={(e) => setNewClient({...newClient, contact_principal: e.target.value})}
                  placeholder="Nom du contact"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                  <Input
                    value={newClient.telephone}
                    onChange={(e) => setNewClient({...newClient, telephone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    placeholder="contact@example.com"
                    type="email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                <Input
                  value={newClient.adresse}
                  onChange={(e) => setNewClient({...newClient, adresse: e.target.value})}
                  placeholder="Adresse complète"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                  <Input
                    value={newClient.ville}
                    onChange={(e) => setNewClient({...newClient, ville: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                  <Input
                    value={newClient.code_postal}
                    onChange={(e) => setNewClient({...newClient, code_postal: e.target.value})}
                    placeholder="75001"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SIRET <span className="text-slate-400 font-normal">(clients pro)</span></label>
                <Input
                  value={newClient.siret}
                  onChange={(e) => setNewClient({...newClient, siret: e.target.value})}
                  placeholder="14 chiffres — ex : 21750001600019"
                  inputMode="numeric"
                  data-testid="new-client-siret"
                />
                <p className="text-[11px] text-slate-500 mt-1">Requis pour Chorus Pro & Pennylane (B2B / collectivités).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <Textarea
                  value={newClient.notes}
                  onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                  placeholder="Notes internes..."
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleAddClient}
                disabled={!newClient.nom.trim()}
                className={`bg-gradient-to-r ${selectedCategory?.bgGradient || 'from-blue-600 to-blue-800'}`}
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !savingEdit && setShowEditModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-client-dialog">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" /> Modifier la fiche
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)} disabled={savingEdit}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom de la collectivité *</label>
                <Input value={editForm.nom}
                  onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                  data-testid="edit-client-nom" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Interlocuteur principal</label>
                <Input value={editForm.contact_principal}
                  onChange={(e) => setEditForm({ ...editForm, contact_principal: e.target.value })}
                  placeholder="Nom du contact"
                  data-testid="edit-client-contact" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                  <Input value={editForm.telephone}
                    onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                    placeholder="01 23 45 67 89"
                    data-testid="edit-client-tel" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input type="email" value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="contact@example.com"
                    data-testid="edit-client-email" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                <Input value={editForm.adresse}
                  onChange={(e) => setEditForm({ ...editForm, adresse: e.target.value })}
                  data-testid="edit-client-adresse" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                  <Input value={editForm.ville}
                    onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                    data-testid="edit-client-ville" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                  <Input value={editForm.code_postal}
                    onChange={(e) => setEditForm({ ...editForm, code_postal: e.target.value })}
                    data-testid="edit-client-cp" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SIRET <span className="text-slate-400 font-normal">(Chorus Pro / Pennylane)</span></label>
                <Input value={editForm.siret || ''}
                  onChange={(e) => setEditForm({ ...editForm, siret: e.target.value })}
                  placeholder="14 chiffres — ex : 21750001600019"
                  inputMode="numeric"
                  data-testid="edit-client-siret" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <Textarea rows={3} value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Notes internes..."
                  data-testid="edit-client-notes" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={savingEdit}>
                Annuler
              </Button>
              <Button onClick={saveClientEdit}
                disabled={savingEdit || !editForm.nom?.trim()}
                className={`bg-gradient-to-r ${selectedCategory?.bgGradient || 'from-blue-600 to-blue-800'} text-white`}
                data-testid="save-edit-client-btn">
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
      {showObservationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Nouvelle observation</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowObservationModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <Textarea
              value={newObservation}
              onChange={(e) => setNewObservation(e.target.value)}
              placeholder="Notez votre observation..."
              rows={5}
              className="mb-4"
              data-testid="new-observation-input"
            />

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowObservationModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddObservation} disabled={!newObservation.trim()}>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Nouvelle alerte</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAlertModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre *</label>
                <Input
                  value={newAlert.titre}
                  onChange={(e) => setNewAlert({...newAlert, titre: e.target.value})}
                  placeholder="Ex: Relancer pour signature devis"
                  data-testid="new-alert-titre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea
                  value={newAlert.description}
                  onChange={(e) => setNewAlert({...newAlert, description: e.target.value})}
                  placeholder="Détails..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priorité</label>
                  <select
                    value={newAlert.priorite}
                    onChange={(e) => setNewAlert({...newAlert, priorite: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  >
                    <option value="low">Basse</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Haute</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date d'échéance</label>
                  <Input
                    type="date"
                    value={newAlert.date_echeance}
                    onChange={(e) => setNewAlert({...newAlert, date_echeance: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAlertModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleAddAlert}
                disabled={!newAlert.titre.trim()}
                className="bg-amber-500 hover:bg-amber-600"
              >
                <Bell className="w-4 h-4 mr-2" />
                Créer l'alerte
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Detail Modal */}
      {showEmailModal && selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${
                  selectedEmail.direction === 'sent' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  {selectedEmail.direction === 'sent' ? (
                    <Send className="w-6 h-6 text-blue-600" />
                  ) : (
                    <Inbox className="w-6 h-6 text-purple-600" />
                  )}
                </div>
                <div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    selectedEmail.direction === 'sent' 
                      ? 'bg-blue-200 text-blue-700' 
                      : 'bg-purple-200 text-purple-700'
                  }`}>
                    {selectedEmail.direction === 'sent' ? 'Email envoyé' : 'Email reçu'}
                  </span>
                  <p className="text-xs text-slate-400 mt-1">
                    {selectedEmail.date ? new Date(selectedEmail.date).toLocaleDateString('fr-FR', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : 'Date inconnue'}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowEmailModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Subject */}
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {selectedEmail.subject || 'Sans objet'}
                </h2>
              </div>
              
              {/* From/To info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-16">De:</span>
                  <span className="text-slate-900 font-medium">{selectedEmail.from}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 w-16">À:</span>
                  <span className="text-slate-900">{Array.isArray(selectedEmail.to) ? selectedEmail.to.join(', ') : selectedEmail.to}</span>
                </div>
              </div>
              
              {/* Body */}
              <div className="border-t pt-4">
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-slate-700 text-sm leading-relaxed bg-white p-4 rounded-lg border border-slate-200">
                    {selectedEmail.body_text || selectedEmail.body_preview || 'Contenu non disponible'}
                  </pre>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEmailModal(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* AI Suggestions Modal */}
      {showAISuggestionsModal && aiSuggestions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Analyse IA des échanges</h2>
                  <p className="text-sm text-slate-500">Suggestions basées sur vos emails</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAISuggestionsModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Summary */}
            {aiSuggestions.summary && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 mb-6 border border-purple-200">
                <h3 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Résumé des échanges
                </h3>
                <p className="text-slate-700 text-sm">{aiSuggestions.summary}</p>
              </div>
            )}
            
            {/* Suggested Alerts */}
            {aiSuggestions.alerts?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-500" />
                  Alertes suggérées ({aiSuggestions.alerts.length})
                </h3>
                <div className="space-y-2">
                  {aiSuggestions.alerts.map((alert, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      alert.priorite === 'high' ? 'bg-red-50 border-red-200' :
                      alert.priorite === 'medium' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900">{alert.titre}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          alert.priorite === 'high' ? 'bg-red-200 text-red-700' :
                          alert.priorite === 'medium' ? 'bg-amber-200 text-amber-700' :
                          'bg-blue-200 text-blue-700'
                        }`}>
                          {alert.priorite === 'high' ? 'Urgente' : alert.priorite === 'medium' ? 'Moyenne' : 'Basse'}
                        </span>
                      </div>
                      {alert.description && (
                        <p className="text-sm text-slate-600 mt-1">{alert.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Suggested Observations */}
            {aiSuggestions.observations?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-500" />
                  Observations suggérées ({aiSuggestions.observations.length})
                </h3>
                <div className="space-y-2">
                  {aiSuggestions.observations.map((obs, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-sm text-slate-700">{obs}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Suggested Actions en attente */}
            {aiSuggestions.actions_en_attente?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-orange-500" />
                  Actions en attente de notre part ({aiSuggestions.actions_en_attente.length})
                </h3>
                <div className="space-y-2">
                  {aiSuggestions.actions_en_attente.map((action, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      action.priorite === 'high' ? 'bg-red-50 border-red-200' :
                      action.priorite === 'medium' ? 'bg-orange-50 border-orange-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900">{action.action}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          action.priorite === 'high' ? 'bg-red-200 text-red-700' :
                          action.priorite === 'medium' ? 'bg-orange-200 text-orange-700' :
                          'bg-blue-200 text-blue-700'
                        }`}>
                          {action.priorite === 'high' ? 'Urgente' : action.priorite === 'medium' ? 'Moyenne' : 'Basse'}
                        </span>
                      </div>
                      {action.contexte && (
                        <p className="text-sm text-slate-500 mt-1 italic">{action.contexte}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAISuggestionsModal(false)}>
                Ignorer
              </Button>
              <Button 
                onClick={handleApplyAISuggestions}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Appliquer les suggestions
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Action en attente Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-orange-500" />
                Nouvelle action en attente
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowActionModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Action à faire *</label>
                <Textarea
                  value={newAction.action}
                  onChange={(e) => setNewAction({...newAction, action: e.target.value})}
                  placeholder="Ex: Envoyer le RIB au client, Préparer devis ravalement..."
                  rows={3}
                  data-testid="new-action-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priorité</label>
                  <select
                    value={newAction.priorite}
                    onChange={(e) => setNewAction({...newAction, priorite: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  >
                    <option value="low">Basse</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Haute</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contexte</label>
                  <Input
                    value={newAction.contexte}
                    onChange={(e) => setNewAction({...newAction, contexte: e.target.value})}
                    placeholder="Ex: Suite au mail du 14/04"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowActionModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleAddAction}
                disabled={!newAction.action.trim()}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter l'action
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Portal Access Modal */}
      {showPortalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-green-500" />
                Créer accès portail
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPortalModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <p className="text-sm text-slate-500 mb-4">
              Le client pourra se connecter sur <strong>/portail</strong> pour consulter ses devis, l'avancement des travaux et soumettre des demandes.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom du contact</label>
                <Input
                  value={portalForm.contact_name}
                  onChange={(e) => setPortalForm({...portalForm, contact_name: e.target.value})}
                  placeholder="Nom du contact"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email de connexion *</label>
                <Input
                  type="email"
                  value={portalForm.email}
                  onChange={(e) => setPortalForm({...portalForm, email: e.target.value})}
                  placeholder="portail@mairie.fr"
                  data-testid="portal-form-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe *</label>
                <Input
                  type="text"
                  value={portalForm.password}
                  onChange={(e) => setPortalForm({...portalForm, password: e.target.value})}
                  placeholder="Mot de passe initial"
                  data-testid="portal-form-password"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowPortalModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreatePortalAccess}
                disabled={!portalForm.email || !portalForm.password || portalSaving}
                className="bg-green-600 hover:bg-green-700"
                data-testid="portal-form-submit"
              >
                {portalSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Créer l'accès
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// Email Domains Settings Dialog
// ==========================================
function EmailDomainsDialog({ client, onClose, onSaved }) {
  const [domains, setDomains] = useState(client.email_domains || []);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const addDomain = () => {
    const d = draft.trim().toLowerCase().replace(/^@+/, '');
    if (!d) return;
    if (domains.includes(d)) return;
    setDomains([...domains, d]);
    setDraft('');
  };

  const removeDomain = (d) => {
    setDomains(domains.filter((x) => x !== d));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cid = client._id || client.contact_id;
      await api.patch(`/gestion-clients/${cid}/email-domains`, { email_domains: domains });
      onSaved(domains);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="email-domains-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-purple-600" /> Domaines email
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-sm text-slate-500 mb-4">
          Ajoute les domaines de <strong className="text-slate-700">{client.nom}</strong> pour récupérer automatiquement <strong>tous</strong> les emails de l'historique correspondant à ces noms de domaine. Exemple : <code className="bg-slate-100 px-1 rounded">manteslaville.fr</code> récupère tout ce qui contient <code className="bg-slate-100 px-1 rounded">@manteslaville.fr</code>.
        </p>

        {/* Domain list */}
        <div className="space-y-2 mb-4">
          {domains.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center py-4">Aucun domaine configuré pour cette collectivité.</p>
          ) : (
            domains.map((d) => (
              <div key={d} className="flex items-center justify-between p-2 bg-purple-50 border border-purple-100 rounded-lg">
                <span className="text-sm font-mono text-purple-900">@{d}</span>
                <button onClick={() => removeDomain(d)} className="text-slate-400 hover:text-red-600" data-testid={`remove-domain-${d}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Add new */}
        <div className="flex gap-2 mb-4">
          <Input
            placeholder="ex: manteslaville.fr"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
            data-testid="domain-input"
          />
          <Button onClick={addDomain} disabled={!draft.trim()} variant="outline" data-testid="add-domain-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
          <p>Email principal du client : <span className="font-mono text-slate-900">{client.email || '— non défini —'}</span></p>
          <p className="mt-1 text-slate-500">L'historique récupère les mails contenant <strong>l'email principal</strong> ou <strong>n'importe quel domaine</strong> ci-dessus.</p>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="save-domains-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

